import React, { useState } from "react";
import MainCard from "../../../components/MainCard";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { BodyViewer } from "./BodyViewer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@emotion/react";
import { render } from "@testing-library/react";

const statusColors = {
  pass: "success",
  fail: "error",
  skip: "warning",
};

const getStatusColor = (status) => {
  if (!status) return "grey";
  return statusColors[status.toLowerCase()] || "grey";
};

const AssertionItem = ({ assertion, idx, theme }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const status = assertion.skipped ? "SKIP" : assertion.error ? "FAIL" : "PASS";
  const statusLabel = assertion.skipped
    ? "SKIPPED"
    : assertion.error
    ? "FAILED"
    : "PASSED";
  const isFail = status === "FAIL";

  return (
    <Box
      key={`assertion-${idx}`}
      sx={{
        px: 2,
        py: isFail ? 1.5 : 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: isFail
          ? theme.palette.mode === "dark"
            ? theme.palette.error.dark + "22"
            : theme.palette.error.light + "22"
          : theme.palette.background.paper,
      }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}>
        <Typography variant="body1" fontWeight={600} sx={{ flex: 1 }}>
          {assertion.assertion || "Unnamed Assertion"}
        </Typography>

        {renderValue(
          {
            type: "chip",
            value: statusLabel,
            color: getStatusColor(status),
          },
          theme
        )}
      </Stack>

      {isFail && (
        <>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}>
              Error Message
            </Typography>
            {renderValue(
              {
                type: "code",
                value: assertion?.error?.message || "-",
              },
              theme
            )}
          </Box>
          <Accordion
            sx={{
              mt: 0.5,
              backgroundColor: "transparent",
              boxShadow: "none",
              "&:before": { display: "none" },
            }}
            disableGutters
            expanded={isExpanded}
            onChange={() => setIsExpanded(!isExpanded)}>
            <AccordionSummary sx={{ px: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                  p: 0.5,
                }}>
                {isExpanded ? "Hide" : "View"} error details
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 0 }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}>
                    Error Type
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 1,
                      borderRadius: 1,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[900]
                          : theme.palette.grey[100],
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                    }}>
                    {assertion?.error?.name}
                  </Box>
                </Box>

                {assertion?.error?.stack && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}>
                      Stack trace
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 1,
                        maxHeight: 180,
                        overflow: "auto",
                        borderRadius: 1,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[900]
                            : theme.palette.grey[100],
                        fontFamily: "monospace",
                        fontSize: "0.7rem",
                      }}>
                      <pre style={{ margin: 0 }}>{assertion.error.stack}</pre>
                    </Box>
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
};

const getHTTPStatusColor = (code) => {
  if (code >= 200 && code < 300) return "success";
  if (code >= 400 && code < 500) return "warning";
  if (code >= 500) return "error";
  return "primary";
};

const methodMeta = (method) => {
  const normalized = (method || "").toUpperCase();
  const colorMap = {
    GET: "info",
    POST: "primary",
    PUT: "warning",
    PATCH: "secondary",
    DELETE: "error",
  };

  return {
    methodLabel: normalized || "N/A",
    methodColor: colorMap[normalized] || "default",
  };
};

const renderBodyBlock = (body, theme) => {
  if (!body) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not provided
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        mt: 1,
        p: 2,
        backgroundColor: theme.palette.background.default,
        borderRadius: 1,
        fontFamily: "Monospace",
      }}>
      <code> {String(body)}</code>
    </Box>
  );
};

const renderValue = ({ type, value, color, unit }, theme) => {
  switch (type) {
    case "chip":
      return (
        <Box
          sx={{
            borderRadius: 2,
            width: "fit-content",
            border: "1px solid",
            borderColor:
              color && theme.palette[color]
                ? theme.palette[color].main
                : theme.palette.grey[500],
            bgcolor:
              color && theme.palette[color]
                ? theme.palette[color].light
                : theme.palette.grey[200],
            display: "inline-block",
          }}>
          <Typography variant="body2" sx={{ px: 1, py: 0.5 }} fontWeight={600}>
            {value !== null && value !== undefined ? (
              value
            ) : (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary">
                N/A
              </Typography>
            )}
          </Typography>
        </Box>
      );
    case "url":
      return value ? (
        <Typography
          component={Link}
          variant="body2"
          target="_blank"
          href={value}
          rel="noopener noreferrer"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: "blue",
          }}>
          {value}
        </Typography>
      ) : (
        <Typography component="span" variant="body2" color="text.secondary">
          N/A
        </Typography>
      );
    case "number":
      return value !== null && value !== undefined ? (
        `${value} ${unit || ""}`.trim()
      ) : (
        <Typography component="span" variant="body2" color="text.secondary">
          N/A
        </Typography>
      );
    case "code":
      return value !== null && value !== undefined ? (
        <Typography
          variant="body2"
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[900]
                : theme.palette.grey[100],
            borderRadius: 1,
            p: 1,
            fontFamily: "monospace",
          }}>
          <code>{String(value)}</code>
        </Typography>
      ) : (
        <Typography component="span" variant="body2" color="text.secondary">
          N/A
        </Typography>
      );
    case "text":
    default:
      return value !== null && value !== undefined ? (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: theme.palette[color]?.main || "inherit",
          }}>
          {String(value)}
        </Typography>
      ) : (
        <Typography component="span" variant="body2" color="text.secondary">
          N/A
        </Typography>
      );
  }
};

const renderKeyValuePairs = (data, theme) => {
  let items = [];

  if (Array.isArray(data)) {
    items = data;
  } else if (data && typeof data === "object") {
    items = Object.entries(data)
      .map(([key, { type, unit, color, value }]) => {
        if (value)
          return {
            key,
            type,
            unit,
            color,
            value,
          };
        return null;
      })
      .filter(Boolean);
  }

  if (!items.length) {
    return (
      <Grid container sx={{ width: "100%" }}>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: "background.default",
            borderRadius: 1,
            p: 1,
          }}>
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container sx={{ width: "100%" }}>
      {items.map(({ key, ...rest }, index) => (
        <Grid
          item
          xs={12}
          md={6}
          key={`${key || "header"}-${index}`}
          sx={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: "background.default",
            borderRadius: 1,
            p: 1,
          }}>
          <Typography variant="caption" fontWeight="bold">
            {key || "Key"}
          </Typography>
          {renderValue(rest, theme)}
        </Grid>
      ))}
    </Grid>
  );
};

const renderTable = (data, theme) => {
  if (!data || !data.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not provided
      </Typography>
    );
  }
  return (
    <Stack sx={{ mt: 1, px: 1 }} direction={"column"}>
      {data.map((header, index) => (
        <Grid
          key={`${header.key || "header"}-${index}`}
          container
          sx={{
            width: "100%",
            borderBottom: index === data.length - 1 ? "none" : "1px solid",
            borderColor: "divider",
            py: 0.5,
            mb: index === data.length - 1 ? 0 : 0.5,
          }}>
          <Grid
            item
            xs={3}
            sx={{
              justifyContent: "flex-start",
              display: "flex",
              alignItems: "center",
            }}>
            <Typography variant="body2" fontWeight="bold">
              {header.key || "Key"}
            </Typography>
          </Grid>
          <Grid item xs={9}>
            {renderValue(
              {
                type: "text",
                value: header.value,
              },
              theme
            )}
          </Grid>
        </Grid>
      ))}
    </Stack>
  );
};

const renderDetailSection = (
  { title, content, url, isTable = false },
  theme
) => (
  <Box
    component="section"
    sx={{
      p: 1,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: theme.palette.background.default,
      height: "100%",
    }}>
    <Typography variant="overline" fontWeight={"bold"}>
      {title}
    </Typography>
    <Divider />
    {url ? (
      <BodyViewer fileUrl={url} />
    ) : isTable ? (
      renderTable(content, theme)
    ) : typeof content === "string" ? (
      renderBodyBlock(content, theme)
    ) : (
      renderKeyValuePairs(content, theme)
    )}
  </Box>
);

const RequestAccordion = ({ execution, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { methodLabel, methodColor } = methodMeta(execution?.request?.method);
  const theme = useTheme();

  return (
    <MainCard
      contentSX={{ padding: "0px 0px !important" }}
      sx={{
        border: "1px solid",
        borderColor: "divider",
      }}
      key={`${execution._id}-${index}`}>
      <Accordion
        key={`${execution._id}-${index}`}
        expanded={isExpanded}
        disableGutters
        onChange={() => setIsExpanded(!isExpanded)}
        TransitionProps={{ unmountOnExit: true }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 30, color: "black" }} />}
          aria-controls={`panel-${execution?._id}-content`}
          id={`panel-${execution?._id}-header`}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderLeft: "6px solid",
            borderRadius: "8px",
            borderLeftColor:
              theme.palette[getStatusColor(execution?.status)].main,
          }}>
          <Grid
            container
            alignItems="right"
            spacing={12}
            sx={{ width: "300%", pr: 0.5 }}>
            <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {execution?.iteration !== null
                  ? `Iteration-${parseInt(execution?.iteration, 10) + 1} `
                  : ""}
                {execution?.name || "Untitled Request"}
              </Typography>
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <Chip
                label={execution?.request?.method || "N/A"}
                size="small"
                color={execution?.request?.method ? methodColor : "default"}
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  minWidth: 60,
                  fontSize: 13,
                  ...(!execution?.request?.method && {
                    color: "text.secondary",
                    borderColor: "text.secondary",
                  }),
                }}
              />
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <Chip
                label={execution?.response?.code || "N/A"}
                size="small"
                color={
                  execution?.status?.toLowerCase() === "pass"
                    ? "success"
                    : execution?.response?.code
                    ? getHTTPStatusColor(execution?.response?.code)
                    : "default"
                }
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  minWidth: 50,
                  fontSize: 15,
                  ...(!execution?.response?.code && {
                    color: "text.secondary",
                    borderColor: "text.secondary",
                  }),
                }}
              />
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                {execution?.response?.responseTime
                  ? `${execution.response.responseTime} ms`
                  : "-"}
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              {execution?.assertions?.length > 0 ? (
                (() => {
                  const failedCount = execution.assertions.filter(
                    (a) => a.error
                  ).length;
                  return (
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={failedCount > 0 ? "error.main" : "success.main"}>
                      {`Assertions: ${
                        failedCount > 0
                          ? `${failedCount}/${execution.assertions.length} Failed`
                          : `${execution.assertions.length}/${execution.assertions.length} Passed`
                      }`}
                    </Typography>
                  );
                })()
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No Assertions
                </Typography>
              )}
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              {renderDetailSection(
                {
                  title: "Request Details",
                  content: {
                    "Request Method": {
                      type: "chip",
                      value: execution?.request?.method || null,
                      color: methodColor,
                    },
                    "Request URL": {
                      type: "url",
                      value: execution?.request?.url || null,
                    },
                  },
                },
                theme
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              {renderDetailSection(
                {
                  title: "Response Details",
                  content: {
                    Status: {
                      type: "text",
                      value: execution?.response?.status,
                    },
                    "Status Code": {
                      type: "chip",
                      value: execution?.response?.code,
                      color: getHTTPStatusColor(execution?.response?.code),
                    },
                    "Response Time (ms)": {
                      type: "number",
                      value: execution?.response?.responseTime,
                      unit: "ms",
                    },
                    "Response Size (bytes)": {
                      type: "number",
                      value: execution?.response?.responseSize,
                      unit: "B",
                    },
                  },
                },
                theme
              )}
            </Grid>

            {execution?.request?.headers && (
              <Grid item xs={12} md={execution?.response?.headers ? 6 : 12}>
                {renderDetailSection(
                  {
                    title: "Request Headers",
                    isTable: true,
                    content: execution?.request?.headers,
                  },
                  theme
                )}
              </Grid>
            )}
            {execution?.response?.headers && (
              <Grid item xs={12} md={execution?.request?.headers ? 6 : 12}>
                {renderDetailSection(
                  {
                    title: "Response Headers",
                    content: execution?.response?.headers,
                    isTable: true,
                  },
                  theme
                )}
              </Grid>
            )}
            {execution?.requestFile && (
              <Grid item xs={12}>
                {renderDetailSection(
                  {
                    title: "Request Body",
                    url: execution?.requestFile,
                  },
                  theme
                )}
              </Grid>
            )}
            {execution?.responseFile && (
              <Grid item xs={12}>
                {renderDetailSection(
                  {
                    title: "Response Body",
                    url: execution?.responseFile,
                  },
                  theme
                )}
              </Grid>
            )}
            {execution?.assertions && execution?.assertions.length > 0 && (
              <Grid item xs={12}>
                <Box
                  component="section"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: theme.palette.background.default,
                  }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 2 }}>
                    Assertions
                  </Typography>
                  <Stack direction="column" spacing={2}>
                    {execution?.assertions.map((assertion, idx) => (
                      <AssertionItem
                        key={`assertion-${idx}`}
                        assertion={assertion}
                        idx={idx}
                        theme={theme}
                      />
                    ))}
                  </Stack>
                </Box>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </MainCard>
  );
};

export default RequestAccordion;
